import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import {
  createPatientsCustomViews,
  deletePatientsCustomView,
  getCitc,
  getAllDeviceOrderings,
  getPatientsCustomViews,
  getPatientsCustomViewsOrder,
  patchPatientsCustomViews,
  patchPatientsCustomViewsOrder,
  fetchAccountAttributes
} from './service';
import { login, consumeAuthorizationCode, logoutWithDelay } from '../oauth/service';

const snakeToCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const normalizeColumnIds = (cols: IColumnPreference[]): IColumnPreference[] =>
  cols.map((col) => ({
    ...col,
    id: col.type === 'measurement' ? col.id : snakeToCamel(col.id),
    type: snakeToCamel(col.type) as IColumnPreferenceType,
    isPreference: String(col.isPreference) === 'true'
  }));

const normalizeFilters = (filters: ITemplatePageFilters[]): ITemplatePageFilters[] =>
  filters.map((filter) => ({
    ...filter,
    filterValue: filter.filterValue?.map((value) => snakeToCamel(value))
  }));

export const initialState: ISettingsState = {
  citc: null,
  loading: false,
  error: false,
  unitPreferences: {} as IUnitPreference,
  defaultClinicalTimeLogTypeId: '',
  automaticClinicalTimeLog: false,
  vitalsDailyOrderPreference: '1',
  columns: {
    patients: []
  },
  customViews: {
    patients: {
      views: [],
      currentView: 0,
      currentViewRows: 0,
      viewsCreatingStatus: undefined,
      viewsUpdatingStatus: undefined,
      viewsDeletingStatus: undefined,
      viewsOrder: null,
      viewsOrderLoadingStatus: undefined
    },
    loading: false,
    error: false
  },
  hideNavigation: false,
  embedded: false,
  deviceOrderings: []
};

const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updateColumns: (
      state,
      action: PayloadAction<{ viewId: number; cols: IColumnPreference[] }>
    ) => {
      const viewIndex = state.customViews.patients.views.findIndex(
        (view) => view.id === action.payload.viewId
      );
      const view = state.customViews.patients.views[viewIndex];
      if (view) {
        view.columnsConfiguration = action.payload.cols;
      }
    },
    setUnitPreferences: (state, action: PayloadAction<{ preferences: IUnitPreference }>) => {
      state.unitPreferences = action.payload.preferences;
    },
    setTimeLogPrefs: (
      state,
      action: PayloadAction<{
        defaultClinicalTimeLogTypeId: string;
        automaticClinicalTimeLog: boolean;
      }>
    ) => {
      if (state) {
        state.defaultClinicalTimeLogTypeId = action.payload.defaultClinicalTimeLogTypeId;
        state.automaticClinicalTimeLog = action.payload.automaticClinicalTimeLog;
      }
    },
    setVitalsDailyOrderPreference: (state, action: PayloadAction<string>) => {
      state.vitalsDailyOrderPreference = action.payload;
    },
    setCurrentPatientsView: (state, action: PayloadAction<{ id: number }>) => {
      state.customViews.patients.currentView = action.payload.id;
    },
    resetCurrentPatientsView: (state) => {
      const allPatientsView = state.customViews.patients.views.find((view) => view.isDefault);
      if (allPatientsView) {
        state.customViews.patients.currentView = allPatientsView.id;
      }
    },
    resetDefaultPatientsViewFilters: (state) => {
      // Reset filters for the 'All Patients' view
      const allPatientsViewIndex = state.customViews.patients.views.findIndex(
        (view) => view.isDefault
      );
      if (allPatientsViewIndex > -1) {
        state.customViews.patients.views[allPatientsViewIndex].filtersParams = [
          {
            filterLabel: 'limit',
            filterValue: ['25']
          }
        ];

        state.customViews.patients.views[allPatientsViewIndex].isDirty = false;
      }
    },
    appendToOpenViews: (state, action: PayloadAction<{ id: number; gid: string }>) => {
      if (state.customViews.patients.viewsOrder) {
        if (state.customViews.patients.viewsOrder.sorted[action.payload.gid]) {
          state.customViews.patients.viewsOrder.sorted[action.payload.gid] =
            state.customViews.patients.viewsOrder.sorted[action.payload.gid].concat(
              action.payload.id
            );
        } else {
          state.customViews.patients.viewsOrder.sorted[action.payload.gid] = [action.payload.id];
        }
      }
    },
    removeFromOpenViews: (state, action: PayloadAction<{ id: number; gid: string }>) => {
      if (state.customViews.patients.viewsOrder?.sorted[action.payload.gid]) {
        state.customViews.patients.viewsOrder.sorted[action.payload.gid] =
          state.customViews.patients.viewsOrder.sorted[action.payload.gid].filter(
            (viewId) => viewId !== action.payload.id
          );
      }
    },
    updatePatientsCustomViewFilterValues: (
      state,
      action: PayloadAction<{ viewId: number; updatedFilters: ITemplatePageFilters[] }>
    ) => {
      const viewIndex = state.customViews.patients.views.findIndex(
        (view) => view.id === action.payload.viewId
      );
      const currentFilters = state.customViews.patients.views[viewIndex]?.filtersParams;
      const updatedFiltersLabels = action.payload.updatedFilters.map((f) => f.filterLabel);
      if (currentFilters && updatedFiltersLabels.length > 0) {
        const unchangedFilters = currentFilters?.filter(
          (f) => !updatedFiltersLabels.includes(f.filterLabel)
        );
        state.customViews.patients.views[viewIndex].filtersParams = [
          ...unchangedFilters,
          ...action.payload.updatedFilters
        ];

        // Don't mark a view as dirty if the updates are related to pagination/search actions
        if (
          updatedFiltersLabels.filter((f) => !['page', 'limit', 'search'].includes(f)).length > 0
        ) {
          state.customViews.patients.views[viewIndex].isDirty = true;
        }
      }
    },
    deletePatientsCustomViewFilters: (
      state,
      action: PayloadAction<{ viewId: number; filtersToDelete: IFilterLabel[] }>
    ) => {
      const viewIndex = state.customViews.patients.views.findIndex(
        (view) => view.id === action.payload.viewId
      );
      const currentFilters = state.customViews.patients.views[viewIndex]?.filtersParams;
      if (currentFilters && action.payload.filtersToDelete.length > 0) {
        const newFilters = currentFilters.filter(
          (f) => !action.payload.filtersToDelete.includes(f.filterLabel)
        );
        state.customViews.patients.views[viewIndex].filtersParams = [...newFilters];

        // Don't mark a view as dirty if the updates are related to pagination/search actions
        if (
          action.payload.filtersToDelete.filter((f) => !['page', 'limit', 'search'].includes(f))
            .length > 0
        ) {
          state.customViews.patients.views[viewIndex].isDirty = true;
        }
      }
    },
    setCurrentPatientsCustomViewRows: (state, action: PayloadAction<{ rows: number }>) => {
      state.customViews.patients.currentViewRows = action.payload.rows;
    },
    hideNavigation: (state) => {
      state.hideNavigation = true;
    },
    flagEmbedded: (state) => {
      state.embedded = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCitc.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getCitc.fulfilled, (state, action) => {
        state.loading = false;
        state.citc = action.payload;
      })
      .addCase(getCitc.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(getPatientsCustomViews.pending, (state) => {
        state.customViews.loading = true;
        state.customViews.error = false;
      })
      .addCase(getAllDeviceOrderings.fulfilled, (state, action) => {
        state.deviceOrderings = action.payload;
      })
      .addCase(getPatientsCustomViews.fulfilled, (state, action) => {
        state.customViews.loading = false;
        state.customViews.error = false;
        state.customViews.patients.views = action.payload.map((view: ICustomViewsPatients) => ({
          ...view,
          columnsConfiguration: normalizeColumnIds(view.columnsConfiguration),
          filtersParams: normalizeFilters(view.filtersParams)
        }));

        // CITC supported measurements merge
        if (state.citc?.measurementClasses?.length) {
          state.customViews.patients.views.forEach((view, idx) => {
            state.customViews.patients.views[idx].columnsConfiguration =
              mergeCitcSupportedMeasurementsWithUserPatientsCustomViewColumns(
                view.columnsConfiguration,
                state.citc?.measurementClasses || []
              );
          });
        }

        // add new columns to the default view
        if (state.columns?.patients?.length) {
          state.customViews.patients.views.forEach((view, idx) => {
            state.customViews.patients.views[idx].columnsConfiguration =
              mergeNewDefaultColumnsWithUserPatientsCustomViewColumns(
                view.columnsConfiguration,
                state.columns.patients || [],
                view.isDefault === 1
              );
          });
        }

        // If currentView is not set, probably after a login
        // If currentView is set but there is no corresponding view loaded, that means the app was probably
        // started from EPIC which persists the local storage between different user sessions.
        // So the views array might be refreshed after the first fetch but currentView is
        // still pointing to a view from the previous logged in user
        if (
          !state.customViews.patients.currentView ||
          !state.customViews.patients.views.some(
            (v) => v.id === state.customViews.patients.currentView
          )
        ) {
          // Set current view to default one
          const allPatientsView = state.customViews.patients.views.find((view) => view.isDefault);
          if (allPatientsView) {
            state.customViews.patients.currentView = allPatientsView.id;
          }
        }
      })
      .addCase(getPatientsCustomViews.rejected, (state) => {
        state.customViews.loading = false;
        state.customViews.error = true;
      })
      .addCase(createPatientsCustomViews.pending, (state) => {
        state.customViews.patients.viewsCreatingStatus = 'pending';
      })
      .addCase(createPatientsCustomViews.rejected, (state) => {
        state.customViews.patients.viewsCreatingStatus = 'rejected';
      })
      .addCase(createPatientsCustomViews.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          // Add view from backend response to local state
          state.customViews.patients.views = state.customViews.patients.views.concat(
            action.payload
          );

          // Add view to views order object
          const gid = action.payload.organizationGid || 'all';
          const groupOrder = state.customViews.patients.viewsOrder?.sorted[gid];
          if (state.customViews.patients.viewsOrder && groupOrder) {
            state.customViews.patients.viewsOrder.sorted[gid] =
              state.customViews.patients.viewsOrder.sorted[gid].concat(action.payload.id);
          }

          // Set current view to the newly created one
          state.customViews.patients.currentView = action.payload.id;

          state.customViews.patients.viewsCreatingStatus = 'fullfilled';
        }
      })
      .addCase(getPatientsCustomViewsOrder.pending, (state) => {
        state.customViews.patients.viewsOrderLoadingStatus = 'pending';
      })
      .addCase(getPatientsCustomViewsOrder.rejected, (state) => {
        state.customViews.patients.viewsOrderLoadingStatus = 'rejected';
      })
      .addCase(getPatientsCustomViewsOrder.fulfilled, (state, action) => {
        if (action.payload) {
          state.customViews.patients.viewsOrder = action.payload;
          state.customViews.patients.viewsOrderLoadingStatus = 'fullfilled';
        }
      })
      .addCase(patchPatientsCustomViewsOrder.pending, (state) => {
        state.customViews.patients.viewsOrderLoadingStatus = 'pending';
      })
      .addCase(patchPatientsCustomViewsOrder.rejected, (state) => {
        state.customViews.patients.viewsOrderLoadingStatus = 'rejected';
      })
      .addCase(patchPatientsCustomViewsOrder.fulfilled, (state, action) => {
        state.customViews.patients.viewsOrder = action.payload;
      })
      .addCase(patchPatientsCustomViews.pending, (state) => {
        state.customViews.patients.viewsUpdatingStatus = 'pending';
      })
      .addCase(patchPatientsCustomViews.rejected, (state) => {
        state.customViews.patients.viewsUpdatingStatus = 'rejected';
      })
      .addCase(patchPatientsCustomViews.fulfilled, (state, action) => {
        if (!state.customViews.patients) {
          return;
        }
        state.customViews.patients.views = state.customViews.patients.views.map((stateView) => {
          // Replace the view we just updated with the server response
          if (stateView.id !== action.payload?.id) {
            return stateView;
          } else if (action.payload.isDefault) {
            return {
              ...action.payload,
              filtersParams: stateView.filtersParams
            };
          } else {
            return action.payload;
          }
        });

        state.customViews.patients.viewsUpdatingStatus = 'fullfilled';
      })
      .addCase(deletePatientsCustomView.pending, (state) => {
        state.customViews.patients.viewsDeletingStatus = 'pending';
      })
      .addCase(deletePatientsCustomView.rejected, (state) => {
        state.customViews.patients.viewsDeletingStatus = 'rejected';
      })
      .addCase(deletePatientsCustomView.fulfilled, (state, action) => {
        if (action.payload) {
          state.customViews.patients.viewsDeletingStatus = 'fullfilled';

          // Remove the view from views array
          state.customViews.patients.views = state.customViews.patients.views.filter(
            (view) => view.id !== action.payload
          );

          const allPatientsView = state.customViews.patients.views.find((view) => view.isDefault);
          if (allPatientsView) {
            state.customViews.patients.currentView = allPatientsView.id;
          }
        }
      })
      .addCase(logoutWithDelay.fulfilled, (state) => {
        return {
          ...initialState,
          citc: state.citc,
          vitalsDailyOrderPreference: state.vitalsDailyOrderPreference
        };
      })
      .addCase(fetchAccountAttributes.fulfilled, (state, action) => {
        action.payload.forEach((attr) => {
          switch (attr.attribute) {
            case 'unitPreferences':
              state.unitPreferences = attr.value as IUnitPreference;
              break;
            case 'defaultClinicalTimeLogTypeId':
              state.defaultClinicalTimeLogTypeId = attr.value as string;
              break;
            case 'automaticClinicalTimeLog':
              state.automaticClinicalTimeLog = attr.value as boolean;
              break;
          }
        });
      })
      .addMatcher(isAnyOf(login.fulfilled, consumeAuthorizationCode.fulfilled), (state, action) => {
        state.columns.patients = normalizeColumnIds(action.payload.columns.patients);
      });
  }
});

export const getUserTimeLogType = (state: IAppState) =>
  state.settings.defaultClinicalTimeLogTypeId || '';

export const getUserAutoTimeLog = (state: IAppState) => state.settings.automaticClinicalTimeLog;

export const getUnitPreferences = (state: IAppState) => state.settings.unitPreferences || {};

export const selectVitalsDailyOrderPreference = (state: IAppState) =>
  state.settings.vitalsDailyOrderPreference || '1';

export const selectAllPatientsCustomViews = (state: IAppState) =>
  state.settings.customViews.patients.views;
export const selectCurrentPatientsCustomView = (state: IAppState) =>
  state.settings.customViews.patients.views?.find(
    (v) => v.id === state.settings.customViews.patients.currentView
  );
export const selectDefaultPatientsCustomView = (state: IAppState) =>
  state.settings.customViews.patients.views.find((view) => view.isDefault);
export const selectCurrentPatientsCustomViewRows = (state: IAppState) =>
  state.settings.customViews.patients.currentViewRows;
export const selectPatientsCustomViewsCreatingStatus = (state: IAppState) =>
  state.settings.customViews.patients.viewsCreatingStatus;
export const selectPatientsCustomViewsUpdatingStatus = (state: IAppState) =>
  state.settings.customViews.patients.viewsUpdatingStatus;
export const selectPatientsCustomViewsDeletingStatus = (state: IAppState) =>
  state.settings.customViews.patients.viewsDeletingStatus;

export const selectPatientsCustomViewsOrder = (state: IAppState) =>
  state.settings.customViews.patients.viewsOrder;
export const selectPatientsCustomViewsOrderLoadingStatus = (state: IAppState) =>
  state.settings.customViews.patients.viewsOrderLoadingStatus;

export const selectCustomViewsLoading = (state: IAppState) => state.settings.customViews.loading;

export const settingsReducer = slice.reducer;
export const {
  setUnitPreferences,
  setTimeLogPrefs,
  setVitalsDailyOrderPreference,
  updateColumns,
  setCurrentPatientsView,
  resetCurrentPatientsView,
  resetDefaultPatientsViewFilters,
  appendToOpenViews,
  removeFromOpenViews,
  updatePatientsCustomViewFilterValues,
  deletePatientsCustomViewFilters,
  setCurrentPatientsCustomViewRows,
  hideNavigation,
  flagEmbedded
} = slice.actions;

const mergeNewDefaultColumnsWithUserPatientsCustomViewColumns = (
  userCols: IColumnPreference[],
  defaultColumns: IColumnPreference[],
  isDefaultView: boolean
) => {
  // remove unsupported or disabled columns
  const userColumnsConfigActiveSupported = userCols.filter((col) => {
    return (
      (col.type === 'measurement' ||
        defaultColumns.some((defaultCol) => defaultCol.id === col.id)) &&
      col.enabled
    );
  });

  // new columns only
  const missingNewDefaultColumns = defaultColumns.filter((defaultCol) => {
    return defaultCol.type !== 'measurement' && !userCols.some((col) => col.id === defaultCol.id);
  });

  // mark new columns as disabled for non default views
  const disabledNewDefaultColumns = missingNewDefaultColumns.map((col) => {
    return { ...col, enabled: false };
  });

  // disabled supported columns
  const userColumnsConfigDisabledSupported = userCols.filter((col) => {
    return (
      (col.type === 'measurement' ||
        defaultColumns.some((defaultCol) => defaultCol.id === col.id)) &&
      col.enabled === false
    );
  });

  return userColumnsConfigActiveSupported.concat(
    isDefaultView ? missingNewDefaultColumns : disabledNewDefaultColumns,
    userColumnsConfigDisabledSupported
  );
};

const mergeCitcSupportedMeasurementsWithUserPatientsCustomViewColumns = (
  userCols: IColumnPreference[],
  supportedMeasurements: any[]
) => {
  const columnsConfigRemoveUnsupportedColumns = userCols.filter((col) => {
    return (
      col.type !== 'measurement' ||
      supportedMeasurements.some(
        (supportedMeasurement) => supportedMeasurement.className === col.id
      )
    );
  });

  const missingColumns =
    supportedMeasurements
      .filter((supportedMeasurement) => {
        return !userCols.some((col) => col.id === supportedMeasurement.className);
      })
      .map((supportedMeasurement) => {
        return {
          id: supportedMeasurement.className,
          title: supportedMeasurement.className,
          type: 'measurement' as IColumnPreferenceType,
          isPreference: false,
          enabled: false,
          isSortable: false,
          chosen: false,
          selected: false
        };
      }) || [];
  return columnsConfigRemoveUnsupportedColumns.concat(missingColumns);
};
